<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons-vue';
import { ColorPicker } from 'vue3-colorpicker';
import pageUrls from '@/router/pageUrls';
import { mapColorsToFullObject } from '@/utils/colorUtils';
import CompanyModal from '@/components/modals/Company-modal.vue';

const { companiesPage } = pageUrls;
const b = bem('company-page');

export default {
  components: {
    PageHeader,
    ArrowLeftOutlined,
    EditOutlined,
    ColorPicker,
    CompanyModal,
  },
  data() {
    return {
      companyId: null,
      // Colors
      companyColors: { color1: '', color2: '', color3: '' },
      colorIds: {},
      colorsLoaded: false,
      colorsSaving: false,
      // Edit modal
      showEditModal: false,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompanyAction: 'companies/getCompany',
      getCompanyColors: 'companies/getCompanyColors',
      saveCompanyColors: 'companies/saveCompanyColors',
    }),
    async loadCompany() {
      await this.getCompanyAction(this.companyId);
    },
    async loadColors() {
      try {
        const colors = await this.getCompanyColors(this.companyId);
        if (colors?.length) {
          const map = mapColorsToFullObject(colors);
          this.companyColors = {
            color1: map.color1?.hex || '',
            color2: map.color2?.hex || '',
            color3: map.color3?.hex || '',
          };
          this.colorIds = {
            color1: map.color1?.id,
            color2: map.color2?.id,
            color3: map.color3?.id,
          };
        }
        this.colorsLoaded = true;
      } catch {
        this.colorsLoaded = true;
      }
    },
    async handleSaveColors() {
      this.colorsSaving = true;
      try {
        await this.saveCompanyColors({
          companyId: this.companyId,
          colors: this.companyColors,
          existingIds: this.colorIds,
        });
        await this.loadColors();
      } finally {
        this.colorsSaving = false;
      }
    },
    navigateBack() {
      this.$router.push(companiesPage);
    },
  },
  async mounted() {
    this.companyId = this.$route.params.id;
    await this.loadCompany();
    // Company users see dashboard; admins redirect to page-content tab
    if (!this.isCompanyUser) {
      this.$router.replace(`${companiesPage}/${this.companyId}/page-content`);
    } else {
      this.loadColors();
    }
  },
  computed: {
    ...mapState({
      currentCompany: (state) => state.companies.currentCompany,
      isCurrentCompanyLoaded: (state) => state.companies.isCurrentCompanyLoaded,
    }),
    ...mapGetters({
      isCompanyUser: 'auth/isCompanyUser',
    }),
  },
};
</script>

<template>
  <!-- Admin: redirects to page-content, this template barely shows -->
  <template v-if="!isCompanyUser">
    <PageHeader title="POD Details">
      <template #extra>
        <a-button @click="navigateBack">
          <ArrowLeftOutlined /> Back to PODs
        </a-button>
      </template>
    </PageHeader>
    <div :class="b()">
      <a-spin :spinning="!isCurrentCompanyLoaded" />
    </div>
  </template>

  <!-- Company user: dashboard with company info + colors -->
  <template v-else>
    <div :class="b('dashboard')">
      <a-spin :spinning="!isCurrentCompanyLoaded">
        <!-- Company header -->
        <div :class="b('dash-header')">
          <div :class="b('dash-info')">
            <img
              v-if="currentCompany?.logoImageUri"
              :src="currentCompany.logoImageUri"
              :alt="currentCompany?.displayName"
              :class="b('dash-logo')"
            />
            <div>
              <h1 :class="b('dash-name')">{{ currentCompany?.displayName }}</h1>
              <p :class="b('dash-desc')">{{ currentCompany?.description }}</p>
              <a-tag>{{ currentCompany?.pageKey }}</a-tag>
            </div>
          </div>
          <a-button type="primary" @click="showEditModal = true">
            <EditOutlined /> Edit Company
          </a-button>
        </div>

        <!-- Colors section -->
        <div :class="b('dash-section')">
          <h3 :class="b('dash-section-title')">Company Colors</h3>
          <p :class="b('dash-section-desc')">
            Choose three colors for your company pages.
          </p>
          <div :class="b('dash-colors')">
            <div :class="b('dash-color-item')">
              <span :class="b('dash-color-label')">Color 1</span>
              <ColorPicker
                v-model:pureColor="companyColors.color1"
                format="hex6"
                :disableAlpha="true"
              />
            </div>
            <div :class="b('dash-color-item')">
              <span :class="b('dash-color-label')">Color 2</span>
              <ColorPicker
                v-model:pureColor="companyColors.color2"
                format="hex6"
                :disableAlpha="true"
              />
            </div>
            <div :class="b('dash-color-item')">
              <span :class="b('dash-color-label')">Color 3</span>
              <ColorPicker
                v-model:pureColor="companyColors.color3"
                format="hex6"
                :disableAlpha="true"
              />
            </div>
          </div>
          <a-button
            type="primary"
            :loading="colorsSaving"
            :class="b('dash-save-btn')"
            @click="handleSaveColors"
          >
            Save Colors
          </a-button>
        </div>
      </a-spin>
    </div>

    <!-- Edit company modal (reuse existing) -->
    <CompanyModal
      v-if="showEditModal"
      v-model:show="showEditModal"
      :company="currentCompany"
      @saved="loadCompany"
    />
  </template>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.company-page {
  margin: 0 20px;

  &__dashboard {
    padding: 24px;
  }

  &__dash-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px;
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 12px;
    margin-bottom: 24px;
  }

  &__dash-info {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  &__dash-logo {
    max-height: 80px;
    max-width: 200px;
    object-fit: contain;
    border-radius: 8px;
  }

  &__dash-name {
    font-size: 24px;
    font-weight: 700;
    color: var(--Text-Primary);
    margin: 0 0 4px;
  }

  &__dash-desc {
    font-size: 14px;
    color: var(--Text-Secondary);
    margin: 0 0 8px;
  }

  &__dash-section {
    padding: 24px;
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 12px;
    margin-bottom: 24px;
  }

  &__dash-section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0 0 4px;
  }

  &__dash-section-desc {
    font-size: 13px;
    color: var(--Text-Secondary);
    margin: 0 0 20px;
  }

  &__dash-colors {
    display: flex;
    gap: 24px;
    margin-bottom: 20px;
  }

  &__dash-color-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__dash-color-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--Text-Secondary);
  }

  &__dash-save-btn {
    margin-top: 4px;
  }
}
</style>
